(function () {
  const MINIMUM_PAYOUT = 20;

  class UserAllianceHero {
    constructor(root) {
      this.root = root;
      this.authButton = root.querySelector('[data-ua-auth-trigger="earning"]');
      this.authButtonLabel = this.authButton?.textContent.trim() || '';
      this.onAuthButtonClick = (event) => {
        if (!window.UserAllianceAuth?.isAuthenticated()) return;
        event.preventDefault();
        window.UserAllianceAuth.clearSession();
      };
      this.authButton?.addEventListener('click', this.onAuthButtonClick);
      this.email = '';
      this.storeCode = '';
      this.accessToken = '';
      this.endpoint = root.dataset.summaryEndpoint || 'https://test-api.engweapp.cn/api/shopify/alliance/payment-details/summary';
      this.salesListEndpoint = root.dataset.salesListEndpoint || 'https://test-api.engweapp.cn/api/shopify/alliance/sales/list';
      this.salesDetailEndpoint = root.dataset.salesDetailEndpoint || 'https://test-api.engweapp.cn/api/shopify/alliance/sales/detail';
      this.rewards = root.querySelector('[data-ua-rewards]');
      this.summaryStatus = root.querySelector('[data-ua-summary-status]');
      this.withdrawButton = root.querySelector('[data-ua-withdraw-button]');
      this.abortController = null;
      this.currency = 'EUR';
      this.orderLimit = 10;
      this.orderOffset = 0;
      this.ordersLoaded = false;
      this.ordersStatus = root.querySelector('[data-ua-orders-status]');
      this.ordersTable = root.querySelector('[data-ua-orders-table]');
      this.orderList = root.querySelector('[data-ua-order-list]');
      this.orderPagination = root.querySelector('[data-ua-order-pagination]');
      this.orderPrev = root.querySelector('[data-ua-order-prev]');
      this.orderNext = root.querySelector('[data-ua-order-next]');
      this.orderPage = root.querySelector('[data-ua-order-page]');
      this.orderDialog = root.querySelector('[data-ua-order-dialog]');
      this.orderDialogTitle = root.querySelector('[data-ua-order-dialog-title]');
      this.orderDialogBody = root.querySelector('[data-ua-order-dialog-body]');
      this.bindOrders();
      this.bindTabs();
      this.activateTab('orders', false);
      this.bindWithdrawButton();
      this.onAuthChange = () => this.syncAuthSession();
      document.addEventListener('user-alliance:auth-change', this.onAuthChange);
      this.syncRewardsSpace();
      if (this.rewards && 'ResizeObserver' in window) {
        this.rewardsObserver = new ResizeObserver(() => this.syncRewardsSpace());
        this.rewardsObserver.observe(this.rewards);
      }
      this.onResize = () => this.syncRewardsSpace();
      window.addEventListener('resize', this.onResize);
    }

    init() {
      this.syncAuthSession();
    }

    syncAuthSession() {
      this.abortController?.abort();
      const session = window.UserAllianceAuth?.getSession() || {};
      const loggedIn = Boolean(session.accessToken && session.email);
      if (this.authButton) this.authButton.textContent = loggedIn ? 'LOG OUT' : this.authButtonLabel;
      this.email = loggedIn ? session.email : '';
      this.accessToken = loggedIn ? String(session.accessToken || '').trim() : '';
      this.storeCode = loggedIn ? String(session.storeCode || '').trim().toUpperCase() : '';
      if (this.storeCode === 'GB') this.storeCode = 'UK';
      this.root.classList.toggle('userAlliance-hero--has-rewards', loggedIn);
      if (this.rewards) this.rewards.hidden = !loggedIn;
      this.root.querySelectorAll('[data-ua-rewards-link]').forEach((link) => { link.hidden = !loggedIn; });
      this.currency = 'EUR';
      ['affiliatePartners', 'amountPending', 'dueIn30Days'].forEach((name) => this.updateMetric(name, 0));
      this.setWithdrawState(0);
      this.setStatus('', false);
      this.activateTab('orders', false);
      this.ordersLoaded = false;
      this.orderOffset = 0;
      this.ordersController?.abort();
      this.orderDetailController?.abort();
      this.closeOrderDialog();
      this.renderOrdersState('Loading orders...', false);
      this.activateTab('orders', loggedIn);
      if (loggedIn && this.summaryStatus) this.loadSummary();
    }

    bindTabs() {
      this.tabs = Array.from(this.root.querySelectorAll('[data-ua-tab]'));
      this.panels = Array.from(this.root.querySelectorAll('[data-ua-panel]'));
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.activateTab(tab.dataset.uaTab));
        tab.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          event.preventDefault();
          const currentIndex = this.tabs.indexOf(tab);
          const direction = event.key === 'ArrowRight' ? 1 : -1;
          const nextIndex = (currentIndex + direction + this.tabs.length) % this.tabs.length;
          this.tabs[nextIndex]?.focus();
          this.activateTab(this.tabs[nextIndex]?.dataset.uaTab);
        });
      });
    }

    activateTab(tabName, revealPanel = true) {
      if (!tabName) return;
      this.tabs.forEach((tab) => {
        const isActive = tab.dataset.uaTab === tabName;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', String(isActive));
        tab.tabIndex = isActive ? 0 : -1;
      });
      this.panels.forEach((panel) => {
        panel.hidden = !revealPanel || panel.dataset.uaPanel !== tabName;
      });
      if (revealPanel && tabName === 'orders' && !this.ordersLoaded) this.loadOrders();
      if (revealPanel && tabName === 'withdrawals') this.root.querySelector('user-alliance-payment-history')?.load();
      this.syncRewardsSpace();
    }

    bindOrders() {
      this.onOrderPrev = () => {
        if (this.orderOffset === 0) return;
        this.orderOffset = Math.max(0, this.orderOffset - this.orderLimit);
        this.loadOrders();
      };
      this.onOrderNext = () => {
        this.orderOffset += this.orderLimit;
        this.loadOrders();
      };
      this.onOrderClick = (event) => {
        const button = event.target.closest?.('[data-ua-order-id]');
        if (button) this.loadOrderDetail(button.dataset.uaOrderId, button);
      };
      this.onOrderDialogClose = () => this.closeOrderDialog();
      this.onOrderDialogBackdrop = (event) => {
        if (event.target === this.orderDialog) this.closeOrderDialog();
      };
      this.orderPrev?.addEventListener('click', this.onOrderPrev);
      this.orderNext?.addEventListener('click', this.onOrderNext);
      this.orderList?.addEventListener('click', this.onOrderClick);
      this.orderDialog?.querySelector('[data-ua-order-dialog-close]')?.addEventListener('click', this.onOrderDialogClose);
      this.orderDialog?.querySelector('[data-ua-order-dialog-ok]')?.addEventListener('click', this.onOrderDialogClose);
      this.orderDialog?.addEventListener('click', this.onOrderDialogBackdrop);
      this.orderDialog?.addEventListener('close', () => this.orderDialogTrigger?.focus());
    }

    bindWithdrawButton() {
      this.withdrawButton?.addEventListener('click', () => {
        if (this.withdrawButton.disabled) return;
        const paymentMethods = document.querySelector('user-alliance-payment-methods');
        if (!paymentMethods || paymentMethods.hidden) return;
        const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        paymentMethods.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        paymentMethods.querySelector('[data-ua-payment-mode]:not(:disabled)')?.focus({ preventScroll: true });
      });
    }

    async requestSales(endpoint, body, signal) {
      const response = await window.UserAllianceAuth.request(endpoint, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal
      });
      const payload = await this.readResponse(response);
      if (!response.ok || payload?.success === false || (payload?.code !== undefined && Number(payload.code) !== 0)) {
        throw new Error(this.getResponseMessage(payload) || 'Unable to load order data.');
      }
      return payload?.data ?? payload;
    }

    async loadOrders() {
      if (!this.storeCode || !this.accessToken) return;
      this.ordersController?.abort();
      const controller = new AbortController();
      this.ordersController = controller;
      this.renderOrdersState('Loading orders...', false);
      try {
        const data = await this.requestSales(this.salesListEndpoint, {
          storeCode: this.storeCode,
          accessToken: this.accessToken,
          email: this.email,
          limit: this.orderLimit,
          offset: this.orderOffset,
          search: '',
          startDate: '',
          endDate: ''
        }, controller.signal);
        if (controller.signal.aborted) return;
        const orders = this.getOrderItems(data);
        const total = this.getOrderTotal(data);
        this.renderOrders(orders);
        this.renderOrderPagination(orders.length, total);
        this.ordersLoaded = true;
      } catch (error) {
        if (error?.name === 'AbortError') return;
        this.ordersLoaded = false;
        this.renderOrdersState('Unable to load orders. Select Order Details to try again.', true);
      } finally {
        this.syncRewardsSpace();
      }
    }

    getOrderItems(data) {
      if (Array.isArray(data)) return data;
      for (const key of ['sales', 'list', 'records', 'items', 'orders']) {
        if (Array.isArray(data?.[key])) return data[key];
      }
      if (data?.data && data.data !== data) return this.getOrderItems(data.data);
      return [];
    }

    getOrderTotal(data) {
      for (const key of ['total', 'totalCount', 'count']) {
        const value = Number(data?.[key]);
        if (Number.isFinite(value) && value >= 0) return value;
      }
      if (data?.data && data.data !== data) return this.getOrderTotal(data.data);
      return null;
    }

    renderOrders(orders) {
      this.orderList?.replaceChildren();
      if (!orders.length) {
        this.renderOrdersState(this.orderOffset ? 'No more orders.' : 'No orders yet.', false);
        return;
      }
      const fragment = document.createDocumentFragment();
      orders.forEach((order) => {
        const row = document.createElement('div');
        row.className = 'userAlliance-hero__order';
        row.setAttribute('role', 'row');
        const date = this.createOrderDateCell(order.orderDate);
        const numberCell = document.createElement('div');
        numberCell.className = 'userAlliance-hero__order-cell';
        numberCell.dataset.label = 'Order';
        const number = document.createElement('button');
        number.type = 'button';
        number.className = 'userAlliance-hero__order-name';
        number.dataset.uaOrderId = String(order.orderId ?? order.saleId ?? order.id ?? '');
        number.textContent = String(order.orderNumber ?? '—');
        number.disabled = !number.dataset.uaOrderId || !Number.isSafeInteger(Number(number.dataset.uaOrderId)) || Number(number.dataset.uaOrderId) < 0;
        numberCell.append(number);
        const amount = this.createOrderCell('Amount', this.formatOrderMoney(order.amount), 'userAlliance-hero__order-total');
        const commission = this.createOrderCell('Commission', this.formatOrderMoney(order.commission), 'userAlliance-hero__order-total');
        const headings = this.ordersTable.querySelectorAll('[role="columnheader"]');
        [date, numberCell, amount, commission].forEach((cell, index) => {
          cell.setAttribute('role', 'cell');
          cell.dataset.label = headings[index]?.textContent.trim() || cell.dataset.label;
        });
        row.append(date, numberCell, amount, commission);
        fragment.append(row);
      });
      this.orderList?.append(fragment);
      if (this.ordersStatus) this.ordersStatus.hidden = true;
      if (this.ordersTable) this.ordersTable.hidden = false;
    }

    createOrderCell(label, value, className = '') {
      const cell = document.createElement('div');
      cell.className = `userAlliance-hero__order-cell${className ? ` ${className}` : ''}`;
      cell.dataset.label = label;
      cell.textContent = value;
      return cell;
    }

    renderOrdersState(message, isError) {
      if (this.ordersTable) this.ordersTable.hidden = true;
      if (this.orderPagination) this.orderPagination.hidden = true;
      if (!this.ordersStatus) return;
      this.ordersStatus.textContent = message;
      this.ordersStatus.hidden = false;
      this.ordersStatus.classList.toggle('is-error', Boolean(isError));
    }

    renderOrderPagination(count, total) {
      if (!this.orderPagination) return;
      const currentPage = Math.floor(this.orderOffset / this.orderLimit) + 1;
      const pageCount = total === null ? null : Math.max(1, Math.ceil(total / this.orderLimit));
      this.orderPrev.disabled = this.orderOffset === 0;
      this.orderNext.disabled = pageCount === null ? count < this.orderLimit : currentPage >= pageCount;
      this.orderPage.textContent = pageCount === null ? `Page ${currentPage}` : `Page ${currentPage} of ${pageCount}`;
      this.orderPagination.hidden = this.orderPrev.disabled && this.orderNext.disabled;
    }

    async loadOrderDetail(orderId, trigger) {
      const numericOrderId = Number(orderId);
      if (orderId === '' || orderId === undefined || orderId === null || !Number.isSafeInteger(numericOrderId) || numericOrderId < 0 || !this.storeCode || !this.accessToken || !this.orderDialog) return;
      this.orderDetailController?.abort();
      const controller = new AbortController();
      this.orderDetailController = controller;
      this.orderDialogTrigger = trigger;
      this.orderDialogTitle.textContent = trigger?.textContent?.trim() || 'Order details';
      this.orderDialogBody.replaceChildren(this.createDialogMessage('Loading order details...'));
      if (!this.orderDialog.open) this.orderDialog.showModal();
      try {
        const data = await this.requestSales(this.salesDetailEndpoint, {
          storeCode: this.storeCode,
          accessToken: this.accessToken,
          orderId: numericOrderId,
          email: this.email
        }, controller.signal);
        if (controller.signal.aborted) return;
        this.renderOrderDetail(data?.data ?? data);
      } catch (error) {
        if (error?.name !== 'AbortError') {
          this.orderDialogBody.replaceChildren(this.createDialogMessage('Unable to load order details. Please close and try again.', true));
        }
      }
    }

    renderOrderDetail(order) {
      const detail = order?.order && typeof order.order === 'object' ? { ...order, ...order.order } : order;
      const content = document.createDocumentFragment();
      const orderNumber = String(detail?.orderNumber ?? this.orderDialogTitle.textContent ?? '—');
      this.orderDialogTitle.textContent = orderNumber;
      const summary = document.createElement('div');
      summary.className = 'userAlliance-hero__order-summary';
      [['Order', orderNumber], ['Order Total', this.formatOrderMoney(detail?.total)], ['Commission', this.formatOrderMoney(detail?.commission)]].forEach(([label, value]) => {
        const item = document.createElement('div');
        const title = document.createElement('strong');
        title.textContent = label;
        const text = document.createElement('span');
        text.textContent = value;
        item.append(title, text);
        summary.append(item);
      });
      content.append(summary);

      const products = this.getOrderItems(detail?.products ?? detail?.productList ?? detail?.lineItems ?? detail?.items ?? []);
      const table = document.createElement('div');
      table.className = 'userAlliance-hero__detail-table';
      const head = document.createElement('div');
      head.className = 'userAlliance-hero__detail-row userAlliance-hero__detail-head';
      ['Product', 'Quantity', 'Price', 'Commission'].forEach((label) => head.append(this.createOrderCell(label, label)));
      table.append(head);
      products.forEach((product) => {
        const row = document.createElement('div');
        row.className = 'userAlliance-hero__detail-row';
        row.append(
          this.createOrderCell('Product', String(product.productName ?? product.name ?? product.title ?? '—')),
          this.createOrderCell('Quantity', String(product.quantity ?? 0)),
          this.createOrderCell('Price', this.formatOrderMoney(product.price ?? product.amount)),
          this.createOrderCell('Commission', this.formatOrderMoney(product.commission))
        );
        table.append(row);
      });
      [['Shipping', detail?.shipping ?? detail?.shippingAmount ?? 0, 'shipping'], ['Total Tax', detail?.tax ?? detail?.totalTax ?? 0, 'tax']].forEach(([label, value, iconName]) => {
        const row = document.createElement('div');
        row.className = 'userAlliance-hero__detail-row userAlliance-hero__detail-extra';
        const labelCell = this.createOrderCell(label, label, 'userAlliance-hero__detail-extra-label');
        const icon = this.orderDialog.querySelector(`[data-ua-order-icon="${iconName}"]`);
        if (icon) labelCell.prepend(icon.content.cloneNode(true));
        row.append(labelCell, this.createOrderCell('', ''), this.createOrderCell(label, this.formatOrderMoney(value)), this.createOrderCell('', ''));
        table.append(row);
      });
      content.append(table);
      const coupon = document.createElement('div');
      coupon.className = 'userAlliance-hero__order-coupon';
      const couponLabel = document.createElement('span');
      couponLabel.textContent = 'Coupon Code';
      const couponValue = document.createElement('span');
      couponValue.textContent = String(detail?.coupons || '—');
      coupon.append(couponLabel, couponValue);
      content.append(coupon);
      this.orderDialogBody.replaceChildren(content);
    }

    createDialogMessage(message, isError = false) {
      const element = document.createElement('p');
      element.className = `userAlliance-hero__dialog-message${isError ? ' is-error' : ''}`;
      element.textContent = message;
      return element;
    }

    closeOrderDialog() {
      this.orderDetailController?.abort();
      if (this.orderDialog?.open) this.orderDialog.close();
    }

    createOrderDateCell(value) {
      const cell = this.createOrderCell('Date', '—');
      if (value === undefined || value === null || value === '') return cell;
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        cell.textContent = String(value);
        return cell;
      }
      const day = document.createElement('time');
      day.dateTime = date.toISOString();
      day.textContent = new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: 'numeric' }).format(date);
      const time = document.createElement('span');
      time.className = 'userAlliance-hero__order-time';
      time.textContent = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).format(date);
      cell.replaceChildren(day, time);
      return cell;
    }

    formatOrderMoney(value) {
      if (value === undefined || value === null || value === '') return '—';
      const amount = Number(value);
      return Number.isFinite(amount) ? this.formatMoney(amount) : String(value);
    }

    async loadSummary() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      const controller = this.abortController;
      this.setStatus('Loading your rewards...', false);
      this.rewards?.setAttribute('aria-busy', 'true');

      try {
        const response = await window.UserAllianceAuth.request(this.endpoint, {
          method: 'POST',
          mode: 'cors',
          credentials: 'omit',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ storeCode: this.storeCode, email: this.email }),
          signal: controller.signal
        });
        const payload = await this.readResponse(response);
        if (controller.signal.aborted) return;
        if (!response.ok || (payload && payload.code !== undefined && Number(payload.code) !== 0)) {
          throw new Error(this.getResponseMessage(payload) || 'Unable to load your rewards.');
        }

        const data = payload?.data && typeof payload.data === 'object' ? payload.data : payload;
        this.currency = String(data?.currency || 'EUR').toUpperCase();
        this.updateMetric('affiliatePartners', this.getNumber(data, ['affiliatePartners', 'affiliatePartnerCount', 'partners', 'amountPaid']));
        const amountPending = this.getNumber(data, ['amountPending']);
        this.updateMetric('amountPending', amountPending);
        this.updateMetric('dueIn30Days', this.getNumber(data, ['dueIn30Days', 'dueIn30days']));
        this.setWithdrawState(amountPending);
        this.setStatus('', false);
      } catch (error) {
        if (error?.name === 'AbortError') return;
        this.setStatus('', true);
      } finally {
        if (controller === this.abortController) this.rewards?.setAttribute('aria-busy', 'false');
      }
    }

    updateMetric(name, value) {
      const target = this.root.querySelector(`[data-ua-value="${name}"]`);
      if (target) target.textContent = this.formatMoney(value);
    }

    setWithdrawState(amountPending) {
      if (!this.withdrawButton) return;
      const enabled = amountPending >= MINIMUM_PAYOUT;
      this.withdrawButton.disabled = !enabled;
      this.withdrawButton.setAttribute('aria-disabled', String(!enabled));
      document.dispatchEvent(new CustomEvent('user-alliance:withdraw-state-change'));
    }

    setStatus(message, isError) {
      if (!this.summaryStatus) return;
      this.summaryStatus.textContent = message;
      this.summaryStatus.hidden = !message;
      this.summaryStatus.classList.toggle('is-error', Boolean(isError));
    }

    syncRewardsSpace() {
      if (!this.rewards || !this.root.classList.contains('userAlliance-hero--has-rewards')) return;
      const overlap = 50;
      const space = Math.max(0, this.rewards.offsetHeight - overlap);
      this.root.style.setProperty('--ua-hero-rewards-space', `${space}px`);
    }

    getNumber(data, keys) {
      for (const key of keys) {
        if (data && data[key] !== undefined && data[key] !== null && data[key] !== '') {
          const value = Number(data[key]);
          if (Number.isFinite(value)) return value;
        }
      }
      return 0;
    }

    formatMoney(value) {
      const amount = Number.isFinite(Number(value)) ? Number(value) : 0;
      const symbols = { EUR: '€', USD: '$', GBP: '£', CAD: 'CA$', AUD: 'A$', CHF: 'CHF ' };
      const symbol = symbols[this.currency] || `${this.currency} `;
      const formatted = amount.toLocaleString(undefined, {
        minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
        maximumFractionDigits: 2
      });
      return `${symbol}${formatted}`;
    }

    async readResponse(response) {
      const text = await response.text();
      if (!text) return {};
      try {
        return JSON.parse(text);
      } catch (error) {
        return { message: text };
      }
    }

    getResponseMessage(payload) {
      if (!payload || typeof payload !== 'object') return '';
      if (typeof payload.msg === 'string' && payload.msg) return payload.msg;
      if (typeof payload.message === 'string' && payload.message) return payload.message;
      if (typeof payload.error === 'string' && payload.error) return payload.error;
      return '';
    }

    destroy() {
      this.abortController?.abort();
      this.ordersController?.abort();
      this.orderDetailController?.abort();
      this.rewardsObserver?.disconnect();
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('user-alliance:auth-change', this.onAuthChange);
      this.authButton?.removeEventListener('click', this.onAuthButtonClick);
      if (this.authButton) this.authButton.textContent = this.authButtonLabel;
      this.orderPrev?.removeEventListener('click', this.onOrderPrev);
      this.orderNext?.removeEventListener('click', this.onOrderNext);
      this.orderList?.removeEventListener('click', this.onOrderClick);
      this.orderDialog?.querySelector('[data-ua-order-dialog-close]')?.removeEventListener('click', this.onOrderDialogClose);
      this.orderDialog?.querySelector('[data-ua-order-dialog-ok]')?.removeEventListener('click', this.onOrderDialogClose);
      this.orderDialog?.removeEventListener('click', this.onOrderDialogBackdrop);
      this.tabs?.forEach((tab) => tab.replaceWith(tab.cloneNode(true)));
      this.withdrawButton?.replaceWith(this.withdrawButton.cloneNode(true));
    }
  }

  const initialise = (root) => {
    if (root.dataset.uaHeroInitialised === 'true') return;
    root.dataset.uaHeroInitialised = 'true';
    const controller = new UserAllianceHero(root);
    root.__userAllianceHero = controller;
    controller.init();
  };

  const initialiseAll = () => document.querySelectorAll('[data-ua-hero]').forEach(initialise);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialiseAll, { once: true });
  } else {
    initialiseAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.matches?.('[data-ua-hero]')
      ? event.target
      : event.target.querySelector?.('[data-ua-hero]');
    if (root) initialise(root);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.('[data-ua-hero]')
      ? event.target
      : event.target.querySelector?.('[data-ua-hero]');
    root?.__userAllianceHero?.destroy();
    if (root) delete root.dataset.uaHeroInitialised;
  });
})();
